import { useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";

export default function EmailCSV() {
  const { "*": tail = "" } = useParams();
  const sealed = useMemo(() => decodeURIComponent(tail || ""), [tail]);
  const API_BASE = "https://app.commercialcleaningleadsforyou.com";

  const [firstValue, setFirstValue] = useState(""); // zipcode (from preview)
  const [fullValue, setFullValue] = useState(""); // sealed token
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null); // { type: "success" | "error", msg: string }
  const [lastFileName, setLastFileName] = useState(null);

  // Load token + preview zipcode (server decrypts token and returns { zipcode })
  useEffect(() => {
    setStatus(null);
    setLastFileName(null);
    if (!sealed) {
      setFirstValue("");
      setFullValue("");
      return;
    }
    setFullValue(sealed);
    (async () => {
      try {
        const resp = await fetch(`${API_BASE}/api/token-preview`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: sealed }),
        });
        if (!resp.ok) throw new Error("Invalid token preview");
        const data = await resp.json();
        setFirstValue(String(data?.zipcode || ""));
      } catch {
        setFirstValue("");
      }
    })();
  }, [sealed]);

  const getFilenameFromHeaders = (resp) => {
    const cd = resp.headers.get("content-disposition");
    if (!cd) return null;
    const m = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(cd);
    const raw = m?.[1] || m?.[2];
    if (!raw) return null;
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  };

  const saveBlob = (blob, filename = "download.csv") => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleDownload = async (e) => {
    e?.preventDefault?.();
    if (!fullValue) {
      setStatus({ type: "error", msg: "Invalid or missing download link." });
      return;
    }
    setIsLoading(true);
    setStatus(null);
    try {
      const resp = await fetch(`${API_BASE}/api/download-csv-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: fullValue }), // sealed only
      });
      if (!resp.ok) {
        const text = await resp.text();
        let message = text;
        try {
          message = JSON.parse(text).error || message;
        } catch {}
        throw new Error(message || `Request failed: ${resp.status}`);
      }
      const blob = await resp.blob();
      const headerName =
        getFilenameFromHeaders(resp) ||
        (firstValue ? `${firstValue}.csv` : "data.csv");
      saveBlob(blob, headerName);
      setLastFileName(headerName);
      setStatus({ type: "success", msg: "Download started successfully." });
    } catch (err) {
      console.error(err);
      setStatus({
        type: "error",
        msg: err?.message || "Error downloading file.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
        <title>Download CSV</title>
      </Helmet>
      <div className="relative bg-[#F8F8F8] overflow-hidden min-h-screen">
        {/* Background floating blurred shapes */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-16 h-16 bg-white/20 rounded-full animate-bounce"></div>
          <div className="absolute top-32 right-20 w-12 h-12 bg-white/15 rounded-full animate-bounce"></div>
          <div className="absolute bottom-20 left-1/4 w-20 h-20 bg-white/10 rounded-full animate-bounce"></div>
          <div className="absolute top-1/2 right-10 w-8 h-8 bg-white/25 rounded-full animate-bounce"></div>
          <div className="absolute bottom-32 right-1/3 w-14 h-14 bg-white/15 rounded-full animate-bounce"></div>
        </div>
        {/* Main content */}
        <div className="relative z-10 flex min-h-screen items-center justify-center px-4">
          <div className="w-full max-w-2xl rounded-2xl bg-gray-100 p-8 backdrop-blur-lg shadow-2xl border-6 border-[#4E6978] min-h-48">
            <p className="mt-4 text-center text-bold text-[#4E6978] text-xl">
              Click below to securely download your CSV file.
            </p>

            {/* Zipcode & filename summary */}
            <div className="mt-6 flex justify-center gap-5">
              <p className="text-lg font-semibold uppercase text-[#4E6978]">
                Zipcode -{" "}
                {firstValue ? (
                  firstValue
                ) : (
                  <span className="text-slate-400">—</span>
                )}
              </p>
            </div>

            {/* Status */}
            {status && (
              <div
                className={`mt-2 rounded-lg border px-4 py-3 text-sm ${
                  status.type === "success"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                    : "border-red-200 bg-red-50 text-red-800"
                }`}
                role="status"
                aria-live="polite"
              >
                {status.msg}
              </div>
            )}

            {/* Action */}
            <div className="mt-4 flex justify-center">
              <button
                onClick={handleDownload}
                disabled={isLoading || !fullValue}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#F09900] px-6 py-3 font-semibold text-white shadow-md transition hover:bg-[#F09900] focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:cursor-not-allowed disabled:bg-[#F09900] cursor-pointer"
                style={{ color: "white" }}
              >
                {isLoading && (
                  <svg
                    className="h-5 w-5 animate-spin"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a 8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z"
                    />
                  </svg>
                )}
                {isLoading ? "Preparing…" : "Download CSV"}
              </button>
            </div>

            <div className="mt-8">
              <p className="mt-6 text-center text-base text-[#4E6978] font-medium">
                If the download does not start automatically, please try again
                later.
              </p>
              {lastFileName && (
                <p className="mt-2 text-center text-sm text-slate-600">
                  Saved as <strong>{lastFileName}</strong>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

